import React, { useEffect, useState } from 'react';
import { RCTIList } from '../ap/RCTIList';
import { RCTIEntry } from '../ap/RCTIEntry';
interface RCTIManagementProps {
  locale?: string;
  currentView: string;
  setCurrentView: (view: string) => void;
}
export function RCTIManagement({
  locale = 'en-US',
  currentView,
  setCurrentView
}: RCTIManagementProps) {
  const [selectedRCTI, setSelectedRCTI] = useState(null);
  const [localView, setLocalView] = useState(currentView);
  // Ensure setCurrentView is defined before using it
  useEffect(() => {
    if (!setCurrentView) {
      console.error('setCurrentView prop is required for RCTIManagement');
      return;
    }
    setLocalView(currentView);
    // Only update if we have a valid function and we're on the management view
    if (currentView === 'rcti-management') {
      setCurrentView('rcti-list');
    }
  }, [currentView, setCurrentView]);
  // Sample RCTIs data
  const [rctis, setRCTIs] = useState([{
    id: 'RCTI-2023-001',
    rctiNumber: 'RCTI-001',
    vendor: {
      id: 'v1',
      name: 'ABC Construction'
    },
    entity: {
      id: 'e1',
      name: 'Head Office'
    },
    site: {
      id: 's1',
      name: 'Main Campus'
    },
    rctiDate: '2023-10-18',
    currency: 'AUD',
    taxIdentifier: '123456789',
    reference: 'CONTRACT-2023-001',
    status: 'Draft',
    subtotal: 5000.0,
    totalTax: 500.0,
    grandTotal: 5500.0,
    taxCodes: ['GST10']
  }, {
    id: 'RCTI-2023-002',
    rctiNumber: 'RCTI-002',
    vendor: {
      id: 'v2',
      name: 'XYZ Services'
    },
    entity: {
      id: 'e1',
      name: 'Head Office'
    },
    site: null,
    rctiDate: '2023-10-15',
    currency: 'AUD',
    taxIdentifier: '987654321',
    reference: 'CONTRACT-2023-002',
    status: 'Sent',
    subtotal: 3500.0,
    totalTax: 350.0,
    grandTotal: 3850.0,
    taxCodes: ['GST10']
  }, {
    id: 'RCTI-2023-003',
    rctiNumber: 'RCTI-003',
    vendor: {
      id: 'v3',
      name: 'Global Contractors'
    },
    entity: {
      id: 'e2',
      name: 'Regional Division'
    },
    site: {
      id: 's3',
      name: 'Manufacturing Plant'
    },
    rctiDate: '2023-10-10',
    currency: 'AUD',
    taxIdentifier: '456789123',
    reference: 'CONTRACT-2023-003',
    status: 'Approved',
    subtotal: 12000.0,
    totalTax: 1200.0,
    grandTotal: 13200.0,
    taxCodes: ['GST10']
  }]);
  const handleSaveRCTI = (rctiData, asDraft = true) => {
    const newRCTI = {
      id: `RCTI-2023-00${rctis.length + 1}`,
      rctiNumber: rctiData.rctiNumber === 'AUTO-GENERATED' ? `RCTI-00${rctis.length + 1}` : rctiData.rctiNumber,
      vendor: rctiData.vendor,
      entity: rctiData.entity,
      site: rctiData.site,
      rctiDate: rctiData.rctiDate,
      currency: rctiData.currency,
      taxIdentifier: rctiData.taxIdentifier,
      reference: rctiData.reference,
      status: asDraft ? 'Draft' : 'Sent',
      subtotal: rctiData.subtotal,
      totalTax: rctiData.totalTax,
      grandTotal: rctiData.grandTotal,
      taxCodes: rctiData.lineItems.map(item => item.taxCode).filter((v, i, a) => a.indexOf(v) === i)
    };
    if (selectedRCTI) {
      // Update existing RCTI
      setRCTIs(rctis.map(rcti => rcti.id === selectedRCTI.id ? {
        ...newRCTI,
        id: selectedRCTI.id
      } : rcti));
    } else {
      // Add new RCTI
      setRCTIs([...rctis, newRCTI]);
    }
    setSelectedRCTI(null);
  };
  const handleGeneratePDF = rcti => {
    console.log(`Generating PDF for RCTI: ${rcti.rctiNumber}`);
    // In a real app, this would trigger a PDF generation process
    alert(`PDF generation for ${rcti.rctiNumber} would start here`);
  };
  const handleSendEmail = rcti => {
    console.log(`Sending email for RCTI: ${rcti.rctiNumber}`);
    // In a real app, this would trigger an email sending process
    alert(`Email for ${rcti.rctiNumber} would be sent here`);
    // Update RCTI status to Sent if it was Draft
    if (rcti.status === 'Draft') {
      setRCTIs(rctis.map(item => item.id === rcti.id ? {
        ...item,
        status: 'Sent'
      } : item));
    }
  };
  const handleViewRCTI = rcti => {
    setSelectedRCTI(rcti);
  };
  const handleEditRCTI = rcti => {
    setSelectedRCTI(rcti);
  };
  const renderView = () => {
    if (!setCurrentView) {
      return <div data-id="element-12809">Error: Unable to manage navigation</div>;
    }
    switch (currentView) {
      case 'rcti-list':
      case 'rcti-management':
        return <RCTIList rctis={rctis} onView={rcti => {
          handleViewRCTI(rcti);
          setCurrentView('rcti-details');
        }} onEdit={rcti => {
          handleEditRCTI(rcti);
          setCurrentView('rcti-entry');
        }} onCreate={() => {
          setSelectedRCTI(null);
          setCurrentView('rcti-entry');
        }} locale={locale} data-id="element-12810" />;
      case 'rcti-entry':
        return <RCTIEntry rcti={selectedRCTI} onSave={(rctiData, asDraft) => {
          handleSaveRCTI(rctiData, asDraft);
          setCurrentView('rcti-list');
        }} onCancel={() => {
          setSelectedRCTI(null);
          setCurrentView('rcti-list');
        }} onGeneratePDF={handleGeneratePDF} onSendEmail={handleSendEmail} locale={locale} data-id="element-12811" />;
      case 'rcti-details':
        return <RCTIEntry rcti={selectedRCTI} onSave={(rctiData, asDraft) => {
          handleSaveRCTI(rctiData, asDraft);
          setCurrentView('rcti-list');
        }} onCancel={() => {
          setSelectedRCTI(null);
          setCurrentView('rcti-list');
        }} onGeneratePDF={handleGeneratePDF} onSendEmail={handleSendEmail} locale={locale} viewOnly={true} data-id="element-12812" />;
      default:
        return <RCTIList rctis={rctis} onView={rcti => {
          handleViewRCTI(rcti);
          setCurrentView('rcti-details');
        }} onEdit={rcti => {
          handleEditRCTI(rcti);
          setCurrentView('rcti-entry');
        }} onCreate={() => {
          setSelectedRCTI(null);
          setCurrentView('rcti-entry');
        }} locale={locale} data-id="element-12813" />;
    }
  };
  return <div className="space-y-6" data-id="element-12814">
      <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6" data-id="element-12815">
        {renderView()}
      </div>
    </div>;
}